import { CANONICAL_EVENTS } from "./canonicalEvents";
import { trackCanonicalEvent } from "./mixpanelTracker";

const SESSION_STARTED_KEY = "mvp_analytics_session_started_v1";
const ATTRIBUTION_CAPTURED_KEY = "mvp_attribution_captured_v1";
const REFERRAL_VISIT_KEY = "mvp_referral_visit_v1";
const LAST_VISIT_KEY = "mvp_last_visit_at";

function roleFromTitle(title?: string): "viewer" | "creator" | "guest" {
  const t = title?.toLowerCase();
  if (t === "viewer") return "viewer";
  if (t === "creator" || t === "model") return "creator";
  return "guest";
}

/**
 * Один раз за життя табу: session_started; повернення — session_returned.
 * Перший захід з UTM або ref у сесії — attribution_recorded; ref — додатково referral_attributed (visit).
 */
export function runSessionAndAttributionBootstrap(params: {
  pathname: string;
  search: string;
  user_id?: string;
  user_type_title?: string;
}): void {
  const { pathname, search, user_id, user_type_title } = params;
  const user_role = roleFromTitle(user_type_title);

  if (typeof window === "undefined") {
    return;
  }

  if (!sessionStorage.getItem(SESSION_STARTED_KEY)) {
    sessionStorage.setItem(SESSION_STARTED_KEY, "1");
    trackCanonicalEvent(
      CANONICAL_EVENTS.SESSION_STARTED,
      {
        mvp_feature: "session_lifecycle",
        path: pathname,
        session_source: "web_app",
      },
      { user_id, user_role }
    );
  }

  const lastRaw = localStorage.getItem(LAST_VISIT_KEY);
  const now = Date.now();
  if (lastRaw) {
    const last = parseInt(lastRaw, 10);
    if (!Number.isNaN(last) && now - last > 60 * 1000) {
      const days_since_last_session = Math.floor(
        (now - last) / (24 * 60 * 60 * 1000)
      );
      if (days_since_last_session >= 1) {
        trackCanonicalEvent(
          CANONICAL_EVENTS.SESSION_RETURNED,
          {
            mvp_feature: "session_lifecycle",
            days_since_last_session,
            path: pathname,
          },
          { user_id, user_role }
        );
      }
    }
  }
  localStorage.setItem(LAST_VISIT_KEY, String(now));

  const sp = new URLSearchParams(search);
  const utm_source = sp.get("utm_source") || undefined;
  const utm_medium = sp.get("utm_medium") || undefined;
  const utm_campaign = sp.get("utm_campaign") || undefined;
  const ref = sp.get("ref") || sp.get("referral") || undefined;

  const hasCampaignParams = Boolean(
    utm_source || utm_medium || utm_campaign
  );

  if (!sessionStorage.getItem(ATTRIBUTION_CAPTURED_KEY)) {
    if (hasCampaignParams || ref) {
      sessionStorage.setItem(ATTRIBUTION_CAPTURED_KEY, "1");
      const attribution_type = ref
        ? "referral"
        : hasCampaignParams
          ? "campaign"
          : "direct";

      trackCanonicalEvent(
        CANONICAL_EVENTS.ATTRIBUTION_RECORDED,
        {
          mvp_feature: "acquisition",
          attribution_type,
          source: utm_source,
          medium: utm_medium,
          campaign: utm_campaign,
          referrer:
            typeof document !== "undefined" ? document.referrer : undefined,
          path: pathname,
        },
        { user_id, user_role }
      );
    }
  }

  if (ref && !sessionStorage.getItem(REFERRAL_VISIT_KEY)) {
    sessionStorage.setItem(REFERRAL_VISIT_KEY, "1");
    trackCanonicalEvent(
      CANONICAL_EVENTS.REFERRAL_ATTRIBUTED,
      {
        mvp_feature: "referral",
        referral_stage: "visit",
        referrer_user_id: ref,
        path: pathname,
      },
      { user_id, user_role }
    );
  }
}
